<template>
  <form class="ui-form" @submit.prevent="onSubmit">
    <UIInputText
      v-model="nameValue"
      :error="nameError"
      label="Name"
      data-refid="repositoryFormName"
      :disabled
      :readonly
      @ready="onReady"
    />
    <UIInputAutocomplete
      v-model="currencyValue"
      :error="currencyError"
      label="Currency"
      data-refid="repositoryFormCurrency"
      :disabled
      :data="currencies"
      :readonly
    />
    <slot />
    <UIButtonsGroup>
      <slot name="buttons" />
      <UIButtonClose label="Close" @click="handleClose" />
      <UIButtonSave
        v-if="isSaveButtonVisible"
        :disabled="isSaveButtonDisabled"
        :loading
        data-refid="repositoryFormSave"
      />
    </UIButtonsGroup>
  </form>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useField, useForm } from 'vee-validate'
import { omit, orderBy } from 'lodash'

import { Repository } from '@types'

import { ACCESS_TYPE_OWNER, ACCESS_TYPE_READONLY } from '@/const'

import { rules } from '@/helpers/validate'

import { useRepositoriesStore } from '@/store/repositories/index'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIInputAutocomplete, UIInputText } from '@ui/fields'

type Props = {
  loading?: boolean
}

type Emits = {
  close: []
  save: [Partial<Repository>]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<Repository>()

const repositoriesStore = useRepositoriesStore()

const { meta, handleSubmit, resetForm } = useForm({
  initialValues: {
    name: modelValue.value?.name || '',
    currency: modelValue.value?.currency || 'USD',
    price_source_precedence:
      modelValue.value?.settings?.price_source_precedence,
  },
  validationSchema: {
    name: rules.required,
    currency: rules.required,
    price_source_precedence: true,
  },
})

defineExpose({
  resetForm(values: any) {
    resetForm({ values })
  },
  updatePriceSourcePrecedence(values: string[]) {
    priceSourcePrecedenceValue.value = values
  },
})

const { value: nameValue, errorMessage: nameError } = useField<string>('name')
const { value: currencyValue, errorMessage: currencyError } =
  useField<string>('currency')
const { value: priceSourcePrecedenceValue } = useField<string[]>(
  'price_source_precedence',
)

const readonly = computed(() => modelValue.value?.user_repo_settings.access_type === ACCESS_TYPE_READONLY)

const currencies = computed(() =>
  orderBy(repositoriesStore.getCurrencies, 'name').map(item => ({
    value: item.ticker,
    label: item.name,
  })),
)

const isOwner = computed(
  () =>
    !modelValue.value?.user_repo_settings ||
    modelValue.value?.user_repo_settings?.access_type === ACCESS_TYPE_OWNER,
)

const isReadonly = computed(
  () => modelValue.value?.user_repo_settings?.access_type === ACCESS_TYPE_READONLY,
)

const disabled = computed(() => props.loading || !isOwner.value)

const isSaveButtonVisible = computed(() => !readonly?.value)

const isSaveButtonDisabled = computed(
  () => props.loading || isReadonly.value || !(meta.value.dirty && meta.value.valid),
)

const onReady = (el: HTMLInputElement) => {
  if (nameValue.value) return
  el.focus()
}

const onSubmit = handleSubmit(values => {
  const data = {
    ...modelValue.value,
    ...omit(values, 'price_source_precedence'),
    ...(values.price_source_precedence
      ? {
        settings: {
          ...modelValue.value?.settings,
          price_source_precedence: values.price_source_precedence,
        },
      }
      : {}),
  }
  emit('save', data)
})

const handleClose = () => {
  emit('close')
}
</script>
